import { type ComponentProps } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { useItemAlign } from './context'

const itemMediaVariants = cva('flex flex-none items-center justify-center overflow-hidden', {
  variants: {
    variant: {
      // A lucide glyph on a quiet tile.
      icon: 'bg-background-subtle text-foreground-secondary corner-squircle size-8 rounded-xl [&>svg]:size-4',
      // A brand mark or product logo in a bordered tile, the way an app icon sits in a list.
      image:
        'bg-background-raised border-border size-10 rounded-lg border [&>img]:size-full [&>img]:object-cover [&>svg]:size-6',
      // A person.
      avatar: 'size-8 rounded-full [&>img]:size-full [&>img]:object-cover',
    },
  },
  defaultVariants: { variant: 'icon' },
})

interface ItemMediaProps extends ComponentProps<'span'>, VariantProps<typeof itemMediaVariants> {
  className?: string
}

/** The leading tile. Hidden from assistive tech by default: the title names the row. */
function ItemMedia({ className, variant, children, ...props }: ItemMediaProps) {
  const align = useItemAlign()
  return (
    <span
      data-slot="item-media"
      aria-hidden="true"
      className={twMerge(itemMediaVariants({ variant }), align === 'start' && 'self-start', className)}
      {...props}
    >
      {children}
    </span>
  )
}

export { ItemMedia, itemMediaVariants }
export type { ItemMediaProps }
